import type {
  AspectRatioProps,
  MotionPropsFor,
  StackProps,
} from '@hover/blueprint';
import {
  AspectRatio,
  Image,
  Motion,
  Stack,
  forwardRef,
  motion,
  useBreakpointValue,
} from '@hover/blueprint';
import type { Breakpoints } from '@hover/blueprint-theme';
import { AnimatePresence } from 'framer-motion';
import React, { useEffect } from 'react';

const MotionAspectRatio = motion(AspectRatio);

const ImageThumbnails = forwardRef<MotionPropsFor<AspectRatioProps>, 'div'>(
  (props, ref) => (
    <MotionAspectRatio
      borderRadius={400}
      mt="0 !important"
      overflow="hidden"
      position="absolute"
      ratio={1}
      shadow="default"
      transition={{ duration: 0.65 }}
      {...props}
      ref={ref}
    />
  ),
);

type Breakpoint = keyof Breakpoints | 'base';

interface ImageCarouselProps extends StackProps {
  images: Record<string, string>;
  breakPointValues:
    | (string | number)[]
    | Partial<Record<Breakpoint, string | number>>;
}

export const ImageCarousel = ({
  images,
  breakPointValues,
  ...props
}: ImageCarouselProps) => {
  const [visibleImages, setVisibleImages] = React.useState(Object.keys(images));
  const imageWidth = useBreakpointValue(breakPointValues);
  const leftImage = visibleImages[0];
  const centerImage = visibleImages[1];
  const rightImage = visibleImages[2];

  useEffect(() => {
    const timer = setInterval(() => {
      setVisibleImages(prevImages => {
        const [first, ...rest] = prevImages;
        return [...rest, first];
      });
    }, 2500);

    return () => {
      clearInterval(timer);
    };
  }, []);

  return (
    <Stack
      alignItems="center"
      flex={{ desktop: 1 }}
      height={imageWidth}
      justifyContent="center"
      position="relative"
      {...props}
    >
      <Motion.Soft>
        <AnimatePresence mode="popLayout">
          <ImageThumbnails
            animate={{ x: '-50%', scale: '65%' }}
            exit={{ x: '0%', scale: '65%' }}
            initial={{ x: '0%', scale: '100%' }}
            key={leftImage}
            width={imageWidth}
          >
            <Image alt="left-image" src={images[leftImage]} />
          </ImageThumbnails>
          <ImageThumbnails
            animate={{ x: '0%', scale: '100%', zIndex: 2 }}
            exit={{ x: '-50%', scale: '65%', zIndex: 1 }}
            initial={{ x: '0%', scale: '100%', zIndex: 2 }}
            key={centerImage}
            width={imageWidth}
          >
            <Image
              alt="center-image"
              position="absolute"
              src={images[centerImage]}
            />
          </ImageThumbnails>
          <ImageThumbnails
            animate={{ x: '50%', scale: '65%', zIndex: 1 }}
            exit={{ x: '0%', scale: '100%', zIndex: 2 }}
            initial={{ x: '0%', scale: '65%', zIndex: 1 }}
            key={rightImage}
            width={imageWidth}
          >
            <Image alt="right-image" src={images[rightImage]} />
          </ImageThumbnails>
        </AnimatePresence>
      </Motion.Soft>
    </Stack>
  );
};
